import React, { useEffect, useMemo, useState } from 'react';
import { InputAdornment, styled } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import GroupIcon from '@mui/icons-material/Group';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CancelRounded from '@mui/icons-material/CancelRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextField from '@mui/material/TextField';
import Box from '@mui/system/Box';
import { TeamInfo } from '../../../api/types';
import './TeamFilter.scss';
import { useFetchTeams } from '../../hooks/useFetch';
import { useFilterContext, useFilterDispatchContext } from '../../contexts/FilterContext';
import SearchDebounce from '../../utils/common/search';

interface EliasNameFilterProps {
  label: string;
  actionType: string;
  actionObject: string;
  apiUrl: string;
  altIcon?: boolean;
}

const StyledAutocomplete = styled(Autocomplete)((props: { textcolor: string, weight: number }) => ({
  '& .MuiInputBase-root': {
    paddingLeft: '35px !important',
    '& .MuiAutocomplete-input': {
      color: props.textcolor,
      fontWeight: props.weight,
    },
    '& .MuiAutocomplete-endAdornment': {    
      '& .MuiAutocomplete-clearIndicator': {
        '& .cancel-icon': {
          color: props.textcolor,
          visibility: 'visible',
        }
      }
    },
  },
}));

function TeamFilter({
  label,
  actionType,
  actionObject,
  apiUrl,
  altIcon,
}: EliasNameFilterProps) {
  const filterContext: any = useFilterContext();
  const filterDispatchContext: any = useFilterDispatchContext();

  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [options, setOptions] = useState<TeamInfo[]>([]);

  const { data: teamData } = useFetchTeams(searchValue, apiUrl);

  const searchDebounce: SearchDebounce = useMemo(() => new SearchDebounce(), [ ]);

  // Clean up this debounce when it is unmounted
  useEffect(() => () => searchDebounce.clear(), [ searchDebounce ]);

  const handleChange = (_event: any, newValue: TeamInfo | null) => {
    setOptions(newValue ? [newValue, ...options] : options);
    filterDispatchContext({
      type: actionType,
      [actionObject]: newValue,
      searchEnabled: true,
    });
  };

  const handleInputChange = (
    _event: any,
    newInputValue: string,
  ) => {
    setInputValue(newInputValue);
    if (newInputValue !== inputValue) {
      searchDebounce.debounce(() => setSearchValue(newInputValue));
    }
  };

  const textColor = filterContext[actionObject] && 
    filterContext[actionObject].id ? '#175CD3' : '#101828';

  const fontweight = filterContext[actionObject] && 
    filterContext[actionObject].id ? 500 : 400;

  return (
    <Box>
      <StyledAutocomplete
        id="elias-team-filter"
        data-testid="elias-team-filter"
        options={teamData?.results ?? []}
        value={filterContext[actionObject]}
        inputValue={inputValue}
        includeInputInList
        filterOptions={(x) => x}
        isOptionEqualToValue={(option: any) =>
          option.franchise_id === filterContext[actionObject]?.franchise_id
        }
        noOptionsText="No matches found."
        getOptionLabel={(option: any) => option.short_name || option.full_name}
        onChange={(event: any, value: any) => handleChange(event, value)}
        onInputChange={handleInputChange}
        renderInput={(params) => (
          <TextField {...params} placeholder={label} InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                { altIcon ? <GroupOutlinedIcon className="group-icon" />  : <GroupIcon className="group-icon" /> }
              </InputAdornment>
            ),
          }}/>
        )}
        renderOption={(props, option: any) => (
          <li {...props} key={option.id}>
            {option.short_name || option.full_name}
          </li>
        )}
        popupIcon={
          <ExpandMoreIcon />
        }
        forcePopupIcon={!filterContext[actionObject]?.id}
        clearIcon={
          <CancelRounded className='cancel-icon' />
        }
        textcolor={textColor}
        weight={fontweight}
      />
    </Box>
  );
}

export default TeamFilter;
